/**
 * Dataset editor (ADR-0019 S5b · ADR-0063 FE-3) — the standalone ROUTE shell for
 * one app projection. The visual query builder is the SHARED `DatasetQueryBuilder`
 * canvas (app-mode: relation-follow joins + column checkboxes + filters/ordina +
 * preview), the SAME component the in-editor `AppDatasetsPane` mounts inline and the
 * one the global datasets use — so there is ONE editor for projections + datasets.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { DatasetQueryBuilder } from "@/components/datasets/DatasetQueryBuilder";
import { appsApi } from "@/lib/api-apps";
import { usePermissions } from "@/lib/permissions";

export function DatasetEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appId = "", datasetId = "" } = useParams<{ appId: string; datasetId: string }>();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");

  // Header chrome only — the canvas fetches the same
  // `["apps",appId,"datasets",datasetId]` query (TanStack dedupes), so we read
  // its name for the page title.
  const dsQ = useQuery({
    queryKey: ["apps", appId, "datasets", datasetId],
    queryFn: () => appsApi.getDataset(appId, datasetId),
    enabled: !!appId && !!datasetId,
  });

  return (
    <PageContainer>
      <PageHeader
        title={dsQ.data?.name ?? t("apps.loading", "Caricamento…")}
        description={t("apps.datasetEditorSubtitle", "Proiezione (join, colonne, filtri)")}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate(`/apps/${appId}`)}>
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.backToApp", "Torna all'app")}
          </Button>
        }
      />
      <PageContent>
        {dsQ.isError ? (
          <Alert tone="danger" title={t("apps.datasetLoadFailed", "Proiezione non caricata")}>
            <span className="font-mono text-xs">{(dsQ.error as Error).message}</span>
          </Alert>
        ) : (
          <div className="flex min-h-[70vh] flex-col">
            <DatasetQueryBuilder appContext={{ appId, datasetId, canWrite }} />
          </div>
        )}
      </PageContent>
    </PageContainer>
  );
}
